import {
  AlarmClock,
  AlertTriangle,
  Baby,
  Banknote,
  BarChart3,
  Briefcase,
  Building2,
  Cake,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Clock,
  CreditCard,
  FileText,
  Globe,
  GraduationCap,
  HandCoins,
  History,
  Hourglass,
  Landmark,
  LayoutDashboard,
  LineChart,
  MapPin,
  PieChart,
  Plane,
  Receipt,
  Scale,
  ScrollText,
  ShieldCheck,
  Sun,
  TrendingUp,
  UserMinus,
  UserPlus,
  UserRound,
  UserX,
  Users,
  UsersRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

export const REPORT_CATEGORIES = [
  'Workforce Overview',
  'Demographics & Diversity',
  'Payroll & Compensation',
  'Statutory & Compliance',
  'Leave & Absence',
  'Attendance & Overtime',
  'Turnover & Retention',
  'Development & Discipline',
  'Custom',
] as const;

export type ReportCategory = (typeof REPORT_CATEGORIES)[number];

export type TemplateColor =
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'violet'
  | 'rose'
  | 'orange'
  | 'cyan'
  | 'teal'
  | 'slate'
  | 'indigo'
  | 'green'
  | 'lime'
  | 'fuchsia'
  | 'purple'
  | 'sky'
  | 'pink'
  | 'red'
  | 'yellow'
  | 'stone';

export interface TemplateColorClasses {
  iconBg: string;
  iconColor: string;
  borderColor: string;
}

/**
 * Full literal class strings so Tailwind's content scanner picks them up.
 */
export const TEMPLATE_COLOR_CLASSES: Record<TemplateColor, TemplateColorClasses> = {
  blue: {
    iconBg: 'bg-blue-500/10',
    iconColor: 'text-blue-600',
    borderColor:
      'border-blue-200 hover:border-blue-400 dark:border-blue-800 dark:hover:border-blue-600',
  },
  emerald: {
    iconBg: 'bg-emerald-500/10',
    iconColor: 'text-emerald-600',
    borderColor:
      'border-emerald-200 hover:border-emerald-400 dark:border-emerald-800 dark:hover:border-emerald-600',
  },
  amber: {
    iconBg: 'bg-amber-500/10',
    iconColor: 'text-amber-600',
    borderColor:
      'border-amber-200 hover:border-amber-400 dark:border-amber-800 dark:hover:border-amber-600',
  },
  violet: {
    iconBg: 'bg-violet-500/10',
    iconColor: 'text-violet-600',
    borderColor:
      'border-violet-200 hover:border-violet-400 dark:border-violet-800 dark:hover:border-violet-600',
  },
  rose: {
    iconBg: 'bg-rose-500/10',
    iconColor: 'text-rose-600',
    borderColor:
      'border-rose-200 hover:border-rose-400 dark:border-rose-800 dark:hover:border-rose-600',
  },
  orange: {
    iconBg: 'bg-orange-500/10',
    iconColor: 'text-orange-600',
    borderColor:
      'border-orange-200 hover:border-orange-400 dark:border-orange-800 dark:hover:border-orange-600',
  },
  cyan: {
    iconBg: 'bg-cyan-500/10',
    iconColor: 'text-cyan-600',
    borderColor:
      'border-cyan-200 hover:border-cyan-400 dark:border-cyan-800 dark:hover:border-cyan-600',
  },
  teal: {
    iconBg: 'bg-teal-500/10',
    iconColor: 'text-teal-600',
    borderColor:
      'border-teal-200 hover:border-teal-400 dark:border-teal-800 dark:hover:border-teal-600',
  },
  slate: {
    iconBg: 'bg-slate-500/10',
    iconColor: 'text-slate-600',
    borderColor:
      'border-slate-200 hover:border-slate-400 dark:border-slate-800 dark:hover:border-slate-600',
  },
  indigo: {
    iconBg: 'bg-indigo-500/10',
    iconColor: 'text-indigo-600',
    borderColor:
      'border-indigo-200 hover:border-indigo-400 dark:border-indigo-800 dark:hover:border-indigo-600',
  },
  green: {
    iconBg: 'bg-green-500/10',
    iconColor: 'text-green-600',
    borderColor:
      'border-green-200 hover:border-green-400 dark:border-green-800 dark:hover:border-green-600',
  },
  lime: {
    iconBg: 'bg-lime-500/10',
    iconColor: 'text-lime-600',
    borderColor:
      'border-lime-200 hover:border-lime-400 dark:border-lime-800 dark:hover:border-lime-600',
  },
  fuchsia: {
    iconBg: 'bg-fuchsia-500/10',
    iconColor: 'text-fuchsia-600',
    borderColor:
      'border-fuchsia-200 hover:border-fuchsia-400 dark:border-fuchsia-800 dark:hover:border-fuchsia-600',
  },
  purple: {
    iconBg: 'bg-purple-500/10',
    iconColor: 'text-purple-600',
    borderColor:
      'border-purple-200 hover:border-purple-400 dark:border-purple-800 dark:hover:border-purple-600',
  },
  sky: {
    iconBg: 'bg-sky-500/10',
    iconColor: 'text-sky-600',
    borderColor:
      'border-sky-200 hover:border-sky-400 dark:border-sky-800 dark:hover:border-sky-600',
  },
  pink: {
    iconBg: 'bg-pink-500/10',
    iconColor: 'text-pink-600',
    borderColor:
      'border-pink-200 hover:border-pink-400 dark:border-pink-800 dark:hover:border-pink-600',
  },
  red: {
    iconBg: 'bg-red-500/10',
    iconColor: 'text-red-600',
    borderColor:
      'border-red-200 hover:border-red-400 dark:border-red-800 dark:hover:border-red-600',
  },
  yellow: {
    iconBg: 'bg-yellow-500/10',
    iconColor: 'text-yellow-600',
    borderColor:
      'border-yellow-200 hover:border-yellow-400 dark:border-yellow-800 dark:hover:border-yellow-600',
  },
  stone: {
    iconBg: 'bg-stone-500/10',
    iconColor: 'text-stone-600',
    borderColor:
      'border-stone-200 hover:border-stone-400 dark:border-stone-800 dark:hover:border-stone-600',
  },
};

export interface ReportTemplate {
  id: string;
  label: string;
  description: string;
  prompt: string;
  icon: LucideIcon;
  color: TemplateColor;
  category: ReportCategory;
  keywords: string[];
}

export const REPORT_TEMPLATES: ReportTemplate[] = [
  // ── Workforce Overview ────────────────────────────────────────────────
  {
    id: 'workforce-summary',
    label: 'Workforce summary',
    description: 'Headcount, departments, status, gender',
    prompt:
      'Generate a workforce summary report. Include total headcount, breakdown by department, employment status, and gender. Add an executive summary, key metrics in tables, and at least one chart (e.g. department distribution). End with brief recommendations.',
    icon: Users,
    color: 'blue',
    category: 'Workforce Overview',
    keywords: ['headcount', 'overview', 'summary', 'employees', 'staff'],
  },
  {
    id: 'department-breakdown',
    label: 'Department breakdown',
    description: 'Counts and % per department',
    prompt:
      'Create a department breakdown report with employee counts and percentages per department. Include a bar chart of headcount by department and a table. Add a short summary of the largest and smallest departments.',
    icon: Building2,
    color: 'emerald',
    category: 'Workforce Overview',
    keywords: ['department', 'team', 'headcount', 'distribution'],
  },
  {
    id: 'location-overview',
    label: 'Location overview',
    description: 'Headcount by office/location',
    prompt:
      'Create a location overview report with employee count per office/location. Include a table and a chart. Summarize the geographic distribution and highlight the main hubs.',
    icon: MapPin,
    color: 'rose',
    category: 'Workforce Overview',
    keywords: ['location', 'office', 'site', 'geography', 'branch'],
  },
  {
    id: 'job-title-breakdown',
    label: 'Job title breakdown',
    description: 'Headcount by job title',
    prompt:
      'Create a job title breakdown report with employee counts per title or role. Include a table and a chart (e.g. bar or pie). Highlight the most common roles and any notable gaps.',
    icon: Briefcase,
    color: 'cyan',
    category: 'Workforce Overview',
    keywords: ['job title', 'role', 'position', 'occupation'],
  },
  {
    id: 'employment-status',
    label: 'Employment status',
    description: 'Full-time, part-time, contract',
    prompt:
      'Generate an employment status report: breakdown by full-time, part-time, contract, or other statuses. Include tables and charts, plus a brief summary of the workforce composition.',
    icon: ClipboardList,
    color: 'teal',
    category: 'Workforce Overview',
    keywords: ['status', 'full-time', 'part-time', 'contract', 'employment type'],
  },
  {
    id: 'upcoming-events',
    label: 'Birthdays & anniversaries',
    description: 'Upcoming employee events',
    prompt:
      'Generate an upcoming employee events report using the get_upcoming_events tool (defaults: birthdays next 15 days, anniversaries next 30 days). List upcoming birthdays and work anniversaries in two separate tables (employee, department, date, and years of service for anniversaries). Add a short summary of how many events fall in the period and any notable milestones (e.g. 5, 10, 15 year anniversaries).',
    icon: Cake,
    color: 'pink',
    category: 'Workforce Overview',
    keywords: ['birthday', 'anniversary', 'events', 'celebration', 'milestone'],
  },
  {
    id: 'employee-profile',
    label: 'Employee profile',
    description: 'Full profile with photo for one employee',
    prompt: `Generate a comprehensive employee profile report for [employee name] using the get_employee_profile tool (and optionally get_employee_pay_history if more pay detail is needed).

Structure the report as follows:
1. Header with the employee's name, code, job title, department, and status. If a picture URL is returned, include it as a markdown image.
2. Personal & contact details table (DOB/age, gender, nationality, email, phone, address).
3. Employment section: join date, tenure, employment type, office, supervisor, work schedule.
4. Compensation snapshot: recent payslips table and payment method (bank masked).
5. Leave balances table and any pending leave requests.
6. Dependents, active loans, qualifications, and disciplinary warnings (omit empty sections).
7. Short summary of notable points for HR follow-up.

Replace [employee name] with the employee you want before generating.`,
    icon: UserRound,
    color: 'indigo',
    category: 'Workforce Overview',
    keywords: [
      'employee',
      'profile',
      'dossier',
      'photo',
      'individual',
      'personnel file',
      'HR file',
    ],
  },
  {
    id: 'full-hr-analytics',
    label: 'Full HR analytics report',
    description: 'Detailed multi-section report',
    prompt: `Generate a comprehensive HR analytics report with the following sections. Use the available tools to fetch real data for each part.

## 1. Executive summary
- Total headcount and key trends
- Top 3 insights in bullet points

## 2. Workforce overview
- Total employees and breakdown by employment status (full-time, part-time, contract)
- Include a table and a chart (e.g. pie or bar)

## 3. Department analysis
- Headcount and percentage per department
- Table and chart (e.g. bar chart by department)
- Short commentary on largest and smallest departments

## 4. Diversity and inclusion
- Gender distribution overall and by department
- Table and at least one chart (e.g. pie for overall, bar for by department)
- Brief commentary on balance

## 5. Tenure and retention
- Tenure buckets (e.g. 0–1 year, 1–3 years, 3–5 years, 5+ years)
- Table and chart
- Insights on retention and experience level

## 6. Geographic distribution
- Employee count by location/office
- Table and chart
- Main hubs and any regional notes

## 7. Key findings and recommendations
- Bullet list of main findings
- 3–5 actionable recommendations

Use markdown throughout. Include at least 3 charts. Be specific with numbers from the data.`,
    icon: ScrollText,
    color: 'indigo',
    category: 'Workforce Overview',
    keywords: ['full report', 'analytics', 'comprehensive', 'multi-section', 'complete'],
  },
  {
    id: 'monthly-hr-dashboard',
    label: 'Monthly HR dashboard',
    description: 'Workforce, payroll, leave, attendance',
    prompt: `Generate a monthly HR dashboard report combining workforce, payroll, leave, and attendance data. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Headline numbers: headcount, total net pay, leave requests pending, overtime hours
- Top 3 insights across all areas

## 2. Workforce snapshot
- Use get_workforce_summary and get_headcount_trend: current headcount, joiners vs leavers this month
- Include a chart of the headcount trend

## 3. Payroll snapshot
- Use get_payroll_summary and get_payroll_cost_by_department: total net pay, basic pay, headcount paid, and cost per department
- Include a bar chart of payroll cost by department

## 4. Leave & absence
- Use get_pending_leave_requests and get_leave_taken_summary: pending approvals and leave taken this month by type
- Include a table and a chart

## 5. Attendance & overtime
- Use get_overtime_summary and get_lateness_summary: overtime hours and lateness by department
- Include at least one chart

## 6. Actions and recommendations
- Bullet list of items needing attention (pending approvals, unusual costs, attendance issues)

Use markdown throughout with tables and at least 3 charts. Be specific with numbers from the data.`,
    icon: LayoutDashboard,
    color: 'violet',
    category: 'Workforce Overview',
    keywords: ['dashboard', 'monthly', 'kpi', 'overview', 'combined', 'executive'],
  },

  // ── Demographics & Diversity ──────────────────────────────────────────
  {
    id: 'gender-diversity',
    label: 'Gender diversity',
    description: 'Gender split by department',
    prompt:
      'Prepare a gender diversity report: overall gender split and distribution by department. Include charts (e.g. pie or bar) and a summary table. Add a brief commentary on balance and any recommendations.',
    icon: UsersRound,
    color: 'violet',
    category: 'Demographics & Diversity',
    keywords: ['gender', 'diversity', 'male', 'female', 'inclusion'],
  },
  {
    id: 'age-demographics',
    label: 'Age demographics',
    description: 'Age bands and average age',
    prompt:
      'Generate an age demographics report using the get_age_demographics tool. Show headcount per age band (e.g. under 25, 25–34, 35–44, 45–54, 55+), the average age overall, and breakdowns by gender and department where available. Include a bar chart of the age bands and a summary table. Comment on workforce ageing risks and succession considerations.',
    icon: PieChart,
    color: 'amber',
    category: 'Demographics & Diversity',
    keywords: ['age', 'demographics', 'generation', 'ageing', 'birthday'],
  },
  {
    id: 'local-expat',
    label: 'Local vs expat',
    description: 'Nationality mix by department',
    prompt:
      'Create a local vs expatriate workforce report using the get_local_expat_breakdown tool. Show counts and percentages of local (Mauritian) vs expatriate employees overall and per department. Include a pie chart for the overall split, a table by department, and a short commentary on the mix, including any departments that rely heavily on expatriate staff.',
    icon: Globe,
    color: 'sky',
    category: 'Demographics & Diversity',
    keywords: ['expat', 'local', 'mauritian', 'nationality', 'foreign'],
  },
  {
    id: 'dependents-summary',
    label: 'Dependents summary',
    description: 'Employee dependents overview',
    prompt:
      'Generate a dependents summary report using the get_dependents_summary tool. Show how many employees have registered dependents, the total number of dependents, and the breakdown by dependent type (spouse, child, etc.). Include a table and a chart, and note the average number of dependents per employee. This is useful for benefits and medical scheme planning.',
    icon: Baby,
    color: 'fuchsia',
    category: 'Demographics & Diversity',
    keywords: ['dependents', 'family', 'spouse', 'children', 'benefits'],
  },
  {
    id: 'diversity-dashboard',
    label: 'Diversity dashboard',
    description: 'Gender, age, and nationality combined',
    prompt: `Generate a combined diversity and demographics dashboard. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Headline diversity metrics and top insights

## 2. Gender diversity
- Use get_gender_diversity: overall split and by department, with a pie chart and table

## 3. Age profile
- Use get_age_demographics: age bands and average age, with a bar chart

## 4. Local vs expatriate
- Use get_local_expat_breakdown: local vs expat mix overall and per department, with a chart

## 5. Findings and recommendations
- Bullet list of observations and 3 actionable recommendations for inclusion and workforce planning

Use markdown with tables and at least 2 charts. Be specific with numbers from the data.`,
    icon: BarChart3,
    color: 'purple',
    category: 'Demographics & Diversity',
    keywords: ['diversity', 'demographics', 'dashboard', 'inclusion', 'combined'],
  },

  // ── Payroll & Compensation ────────────────────────────────────────────
  {
    id: 'payroll-summary',
    label: 'Payroll summary',
    description: 'Net pay, basic, taxable totals',
    prompt:
      'Generate a payroll summary report for the latest closed period using the get_payroll_summary tool. Include total net pay, total basic salary, total taxable pay, and the number of employees paid. Present the key figures in a table, add a chart comparing the main totals, and finish with a short executive summary of the payroll run and anything unusual worth flagging.',
    icon: Wallet,
    color: 'green',
    category: 'Payroll & Compensation',
    keywords: ['payroll', 'net pay', 'salary', 'monthly', 'pay run'],
  },
  {
    id: 'payroll-cost-by-department',
    label: 'Payroll cost by department',
    description: 'Salary spend per department',
    prompt:
      'Create a payroll cost by department report using the get_payroll_cost_by_department tool for the latest period. Show total net pay and basic salary per department with percentages of the overall payroll. Include a bar chart of cost by department and a table sorted by cost. Highlight the most and least expensive departments and comment on cost per head where possible.',
    icon: Banknote,
    color: 'emerald',
    category: 'Payroll & Compensation',
    keywords: ['payroll cost', 'department', 'spend', 'budget', 'labour cost'],
  },
  {
    id: 'salary-distribution',
    label: 'Salary distribution',
    description: 'Headcount per salary band',
    prompt:
      'Generate a salary distribution report using the get_salary_distribution tool. Show the number of employees in each basic-salary band, with a bar chart of the distribution and a table of band, headcount, and percentage. Comment on where most employees sit, any outliers at the top or bottom, and implications for pay structure and equity.',
    icon: BarChart3,
    color: 'lime',
    category: 'Payroll & Compensation',
    keywords: ['salary bands', 'distribution', 'pay range', 'compensation', 'equity'],
  },
  {
    id: 'earnings-deductions',
    label: 'Earnings & deductions',
    description: 'Payslip line-item breakdown',
    prompt:
      'Create an earnings and deductions breakdown report using the get_earnings_deductions_breakdown tool for the latest period. Split the payslip line items into earnings and deductions, showing the total per item (e.g. basic, overtime, allowances, PAYE, pension) including employer contributions where available. Include a table for each side and a chart of the largest components. Summarize what makes up gross pay and where deductions go.',
    icon: Receipt,
    color: 'teal',
    category: 'Payroll & Compensation',
    keywords: ['earnings', 'deductions', 'allowances', 'payslip', 'gross pay'],
  },
  {
    id: 'employee-pay-history',
    label: 'Employee pay history',
    description: "One employee's pay over time",
    prompt:
      "Generate a pay history report for [employee name] using the get_employee_pay_history tool covering the last 6 months. Show each month's basic pay, gross earnings, deductions, and net pay in a table, and include a line chart of net pay over time. Comment on any changes month to month (raises, one-off payments, unusual deductions). Replace [employee name] above with the employee you want before generating.",
    icon: History,
    color: 'sky',
    category: 'Payroll & Compensation',
    keywords: ['pay history', 'employee', 'payslip', 'individual', 'trend'],
  },
  {
    id: 'payroll-periods-status',
    label: 'Payroll periods status',
    description: 'Open vs closed pay periods',
    prompt:
      'Create a payroll periods status report using the get_payroll_periods_status tool for the current year. List each period with its status (open or closed) and salary payment date in a table, and summarize how many periods are closed vs still open. Flag any periods that appear overdue for closing and note the upcoming salary date.',
    icon: CalendarRange,
    color: 'orange',
    category: 'Payroll & Compensation',
    keywords: ['periods', 'pay period', 'open', 'closed', 'calendar', 'salary date'],
  },
  {
    id: 'payment-method-breakdown',
    label: 'Payment methods',
    description: 'Bank transfer vs cash/cheque',
    prompt:
      'Generate a payment method breakdown report using the get_payment_method_breakdown tool. Show how many employees are paid by bank transfer vs cash or cheque, and the distribution of bank transfers per bank. Include a pie chart of payment methods and a table of banks with employee counts. Comment on any employees still paid in cash and the operational or compliance implications.',
    icon: CreditCard,
    color: 'cyan',
    category: 'Payroll & Compensation',
    keywords: ['payment method', 'bank', 'transfer', 'cash', 'cheque'],
  },
  {
    id: 'loans-summary',
    label: 'Employee loans',
    description: 'Active loans and balances',
    prompt:
      'Create an employee loans summary report using the get_loans_summary tool. Show the number of active loans, total amount loaned, total repaid, and total outstanding. Include a table of loans (employee, original amount, repaid, outstanding) and a chart of outstanding balances. Highlight the largest outstanding balances and any loans close to full repayment.',
    icon: HandCoins,
    color: 'yellow',
    category: 'Payroll & Compensation',
    keywords: ['loans', 'advance', 'repayment', 'outstanding', 'debt'],
  },
  {
    id: 'payroll-dashboard',
    label: 'Payroll cost dashboard',
    description: 'Full payroll picture for the month',
    prompt: `Generate a complete payroll cost dashboard for the latest period. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Total net pay, headcount paid, and top insights

## 2. Payroll totals
- Use get_payroll_summary: net pay, basic, taxable totals in a table with a chart

## 3. Cost by department
- Use get_payroll_cost_by_department: table sorted by cost and a bar chart

## 4. Earnings and deductions
- Use get_earnings_deductions_breakdown: largest earning and deduction components with a chart

## 5. Statutory contributions
- Use get_statutory_contributions: employee and employer statutory totals in a table

## 6. Findings and recommendations
- Cost drivers, anomalies, and 3 recommendations

Use markdown with tables and at least 3 charts. Be specific with numbers from the data.`,
    icon: LayoutDashboard,
    color: 'green',
    category: 'Payroll & Compensation',
    keywords: ['payroll dashboard', 'cost', 'combined', 'monthly', 'overview'],
  },

  // ── Statutory & Compliance ────────────────────────────────────────────
  {
    id: 'statutory-contributions',
    label: 'Statutory contributions',
    description: 'PAYE, CSG, NSF totals',
    prompt:
      'Generate a statutory contributions report for the latest period using the get_statutory_contributions tool. Show each statutory line (e.g. PAYE, CSG, NSF) with employee and employer amounts and the combined total payable. Include a table and a bar chart comparing the contribution types. Summarize the total statutory cost for the month and flag anything unusual compared to headcount.',
    icon: Landmark,
    color: 'red',
    category: 'Statutory & Compliance',
    keywords: ['statutory', 'paye', 'csg', 'nsf', 'npf', 'tax', 'contributions'],
  },
  {
    id: 'public-holidays',
    label: 'Public holidays',
    description: 'Holiday calendar for the year',
    prompt:
      'Create a public holidays report for the current year using the get_public_holidays tool. List all public holidays with their dates and days of the week in a table, grouped by month or quarter. Note how many fall on weekends, and highlight upcoming holidays in the next 60 days that payroll and rostering should plan for.',
    icon: Sun,
    color: 'yellow',
    category: 'Statutory & Compliance',
    keywords: ['public holiday', 'calendar', 'holidays', 'year', 'planning'],
  },
  {
    id: 'compliance-overview',
    label: 'Compliance overview',
    description: 'Statutory, periods, and warnings',
    prompt: `Generate an HR and payroll compliance overview report. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Overall compliance posture and top items needing attention

## 2. Statutory contributions
- Use get_statutory_contributions: statutory lines with employee/employer amounts in a table and chart

## 3. Payroll periods
- Use get_payroll_periods_status: open vs closed periods, flag anything overdue

## 4. Disciplinary record
- Use get_disciplinary_warnings: warnings issued recently, by type

## 5. Public holidays
- Use get_public_holidays: upcoming holidays to plan around

## 6. Actions
- Bullet list of compliance actions and deadlines

Use markdown with tables and at least 1 chart. Be specific with numbers from the data.`,
    icon: ShieldCheck,
    color: 'slate',
    category: 'Statutory & Compliance',
    keywords: ['compliance', 'audit', 'statutory', 'overview', 'combined'],
  },

  // ── Leave & Absence ───────────────────────────────────────────────────
  {
    id: 'leave-balances',
    label: 'Leave balances',
    description: 'Balances per employee and type',
    prompt:
      'Generate a leave balances report using the get_leave_balances tool. Show available remaining days per employee and leave type (allowed entitlement, rollover, and taken), with a table sorted by lowest available balance. Flag employees with low or exhausted available balances and those with unusually high available days who should be encouraged to take leave. Include a chart of average available balance by leave type and a short summary of leave liability.',
    icon: Plane,
    color: 'sky',
    category: 'Leave & Absence',
    keywords: ['leave balance', 'annual leave', 'sick leave', 'accrual', 'entitlement'],
  },
  {
    id: 'leave-taken',
    label: 'Leave taken',
    description: 'Approved leave by type and dept',
    prompt:
      'Create a leave taken summary report using the get_leave_taken_summary tool for the last 3 months. Days come from timesheets and additional leave (half days count as 0.5). Show leave grouped by leave type and by department, with total days per group. Include a bar chart of days by leave type, a table by department, and a short commentary on which teams have taken the most leave and any seasonal patterns.',
    icon: CalendarCheck,
    color: 'emerald',
    category: 'Leave & Absence',
    keywords: ['leave taken', 'vacation', 'absence', 'approved', 'days off'],
  },
  {
    id: 'pending-leave',
    label: 'Pending leave requests',
    description: 'Requests awaiting approval',
    prompt:
      'Generate a pending leave requests report using the get_pending_leave_requests tool. List every unapproved request with the employee, department, leave type, requested dates, and duration in a table sorted by start date. Highlight requests starting soon that need urgent decisions, and summarize the total count and days pending per department.',
    icon: Hourglass,
    color: 'amber',
    category: 'Leave & Absence',
    keywords: ['pending', 'approval', 'requests', 'awaiting', 'leave request'],
  },
  {
    id: 'absenteeism',
    label: 'Absenteeism report',
    description: 'Absence days and rates',
    prompt:
      'Create an absenteeism report using the get_absenteeism_report tool for the latest month. Show absence days per employee and per department, including unexplained absences where available. Include a bar chart of absence days by department, a table of the employees with the most absences, and an estimated absenteeism rate. Comment on patterns and suggest follow-up actions.',
    icon: UserX,
    color: 'red',
    category: 'Leave & Absence',
    keywords: ['absenteeism', 'absence', 'missing', 'no show', 'sick'],
  },
  {
    id: 'leave-overview',
    label: 'Leave & absence overview',
    description: 'Balances, usage, and pending',
    prompt: `Generate a combined leave and absence overview report. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Key leave metrics and items needing attention

## 2. Leave balances
- Use get_leave_balances: balances by leave type, flag low and excessive balances

## 3. Leave taken
- Use get_leave_taken_summary: leave taken in the last 3 months by type and department, with a chart

## 4. Pending requests
- Use get_pending_leave_requests: outstanding approvals in a table

## 5. Absenteeism
- Use get_absenteeism_report: absence days by department with a chart

## 6. Recommendations
- 3–5 actions on leave liability, approvals, and absence management

Use markdown with tables and at least 2 charts. Be specific with numbers from the data.`,
    icon: CalendarDays,
    color: 'teal',
    category: 'Leave & Absence',
    keywords: ['leave overview', 'absence', 'dashboard', 'combined', 'liability'],
  },

  // ── Attendance & Overtime ─────────────────────────────────────────────
  {
    id: 'overtime-summary',
    label: 'Overtime summary',
    description: 'OT and night hours by dept',
    prompt:
      'Generate an overtime summary report using the get_overtime_summary tool for the latest month. Show total overtime and night hours by department, with a bar chart and a table including the employees or teams with the most overtime. Estimate the trend versus a normal month if data allows, and comment on workload balance and potential burnout or cost concerns.',
    icon: Clock,
    color: 'orange',
    category: 'Attendance & Overtime',
    keywords: ['overtime', 'ot', 'night hours', 'extra hours', 'workload'],
  },
  {
    id: 'lateness-summary',
    label: 'Lateness report',
    description: 'Late arrivals and top offenders',
    prompt:
      'Create a lateness report using the get_lateness_summary tool for the latest month. Show total lateness hours by department and a table of the employees with the most lateness. Include a bar chart by department, comment on any patterns (specific days or teams), and suggest follow-up actions for repeat offenders.',
    icon: AlarmClock,
    color: 'rose',
    category: 'Attendance & Overtime',
    keywords: ['lateness', 'late', 'punctuality', 'tardiness', 'arrival'],
  },
  {
    id: 'daily-attendance',
    label: 'Daily attendance',
    description: "Today's presence snapshot",
    prompt:
      'Generate a daily attendance summary report using the get_daily_attendance_summary tool. Show who is present, absent, or on leave today, with counts overall and by department. Include a table and a simple chart of the attendance split, and flag departments with notably low presence today.',
    icon: CalendarCheck,
    color: 'blue',
    category: 'Attendance & Overtime',
    keywords: ['attendance', 'daily', 'present', 'today', 'roll call'],
  },
  {
    id: 'attendance-dashboard',
    label: 'Attendance & OT dashboard',
    description: 'Overtime, lateness, absence combined',
    prompt: `Generate a combined attendance and overtime dashboard for the latest month. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Headline attendance metrics and top concerns

## 2. Overtime
- Use get_overtime_summary: overtime and night hours by department with a bar chart

## 3. Lateness
- Use get_lateness_summary: lateness by department and top offenders in a table

## 4. Absenteeism
- Use get_absenteeism_report: absence days by department with a chart

## 5. Findings and recommendations
- Patterns across overtime, lateness, and absence, plus 3 recommended actions

Use markdown with tables and at least 2 charts. Be specific with numbers from the data.`,
    icon: LayoutDashboard,
    color: 'indigo',
    category: 'Attendance & Overtime',
    keywords: ['attendance dashboard', 'overtime', 'lateness', 'combined', 'timesheet'],
  },

  // ── Turnover & Retention ──────────────────────────────────────────────
  {
    id: 'tenure-analysis',
    label: 'Tenure analysis',
    description: 'Length of service and retention',
    prompt:
      'Generate a tenure analysis report showing how long employees have been with the company. Use tenure buckets (e.g. 0–1 year, 1–3 years, 3–5 years, 5+ years). Include a chart and table, plus insights on retention.',
    icon: CalendarClock,
    color: 'amber',
    category: 'Turnover & Retention',
    keywords: ['tenure', 'service', 'seniority', 'experience', 'retention'],
  },
  {
    id: 'recent-departures',
    label: 'Recent departures',
    description: 'Leavers in the last period',
    prompt:
      'Generate a report on recent employee departures. Include count by period, reasons if available, and breakdown by department. Add a short summary and any retention-related insights.',
    icon: UserMinus,
    color: 'orange',
    category: 'Turnover & Retention',
    keywords: ['departures', 'leavers', 'attrition', 'exits', 'resignation'],
  },
  {
    id: 'headcount-trend',
    label: 'Headcount trend',
    description: 'Joiners vs leavers over time',
    prompt:
      'Create a headcount trend report using the get_headcount_trend tool over the last 12 months. Show joiners, leavers, and net change per month in a table, with a line or bar chart of the trend and the monthly turnover rate. Comment on the overall direction, the months with the biggest movements, and what the trend means for hiring plans.',
    icon: LineChart,
    color: 'blue',
    category: 'Turnover & Retention',
    keywords: ['headcount trend', 'growth', 'turnover rate', 'joiners', 'leavers'],
  },
  {
    id: 'turnover-by-group',
    label: 'Turnover by group',
    description: 'Monthly turnover by office, department, or payroll group',
    prompt:
      'Generate a turnover report using get_headcount_trend with grouping. Use startDate and endDate for the requested period (or the last 12 months if not specified). Group by department unless the user asks for office, payroll group (pass payrollGroupCategory), or a setting field (pass settingField). For each group, show a table of joiners, leavers, headcount, and monthly turnover rate, plus a chart comparing groups. Include company-wide totals and highlight the groups with the highest turnover.',
    icon: LineChart,
    color: 'orange',
    category: 'Turnover & Retention',
    keywords: ['turnover', 'grouped', 'department', 'office', 'payroll group', 'by team'],
  },
  {
    id: 'new-joiners',
    label: 'New joiners',
    description: 'Recent hires and onboarding',
    prompt:
      'Generate a new joiners report using the get_new_joiners tool for the last 90 days. List each new hire with their name, department, job title, and start date in a table, and include a chart of hires per department. Summarize hiring momentum, which teams are growing fastest, and any onboarding follow-ups worth noting (e.g. probation reviews coming up).',
    icon: UserPlus,
    color: 'green',
    category: 'Turnover & Retention',
    keywords: ['new joiners', 'hires', 'onboarding', 'recruitment', 'starters'],
  },
  {
    id: 'turnover-retention',
    label: 'Turnover & retention report',
    description: 'Trend, departures, and tenure',
    prompt: `Generate a combined turnover and retention report. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Turnover rate, net headcount change, and top insights

## 2. Headcount trend
- Use get_headcount_trend over the last 12 months: joiners vs leavers per month with a chart and the monthly turnover rate

## 3. Recent departures
- Use get_recent_departures: who left recently, by department, with reasons where available

## 4. Tenure profile
- Use get_tenure_analysis: tenure buckets with a chart, and what they imply for retention risk

## 5. New joiners
- Use get_new_joiners: recent hires and which teams are backfilling vs growing

## 6. Findings and recommendations
- Retention risks and 3–5 actionable recommendations

Use markdown with tables and at least 2 charts. Be specific with numbers from the data.`,
    icon: TrendingUp,
    color: 'purple',
    category: 'Turnover & Retention',
    keywords: ['turnover', 'retention', 'attrition', 'combined', 'churn'],
  },

  // ── Development & Discipline ──────────────────────────────────────────
  {
    id: 'disciplinary-warnings',
    label: 'Disciplinary warnings',
    description: 'Warnings by type and period',
    prompt:
      'Generate a disciplinary warnings report using the get_disciplinary_warnings tool for the last 12 months. Show warnings grouped by type and by month, with a table of recent warnings (employee, department, type, date) and a chart of warnings over time or by type. Flag employees with multiple warnings and comment on any concerning trends by department.',
    icon: AlertTriangle,
    color: 'red',
    category: 'Development & Discipline',
    keywords: ['warnings', 'disciplinary', 'misconduct', 'hr cases', 'discipline'],
  },
  {
    id: 'qualifications-breakdown',
    label: 'Qualifications breakdown',
    description: 'Education and skills profile',
    prompt:
      'Create a qualifications breakdown report using the get_qualifications_breakdown tool. Show the distribution of education levels and qualifications across the workforce, with a bar or pie chart and a table of qualification counts. Comment on skill concentrations, gaps relative to the roles in the organisation, and where training investment could help.',
    icon: GraduationCap,
    color: 'indigo',
    category: 'Development & Discipline',
    keywords: ['qualifications', 'education', 'skills', 'degrees', 'training'],
  },
  {
    id: 'development-overview',
    label: 'People development overview',
    description: 'Qualifications and conduct combined',
    prompt: `Generate a people development and conduct overview report. Use the available tools to fetch real data for each section:

## 1. Executive summary
- Workforce capability highlights and conduct items needing attention

## 2. Qualifications profile
- Use get_qualifications_breakdown: education levels and qualification distribution with a chart

## 3. Disciplinary record
- Use get_disciplinary_warnings for the last 12 months: warnings by type with a table, flag repeat cases

## 4. Findings and recommendations
- Training and development priorities, plus any conduct follow-ups

Use markdown with tables and at least 1 chart. Be specific with numbers from the data.`,
    icon: Scale,
    color: 'stone',
    category: 'Development & Discipline',
    keywords: ['development', 'capability', 'conduct', 'combined', 'training'],
  },

  // ── Custom ────────────────────────────────────────────────────────────
  {
    id: 'custom-report',
    label: 'Custom report',
    description: 'Your own description',
    prompt:
      'Generate a comprehensive HR analytics report. Use available data tools to gather workforce metrics, department breakdowns, and diversity stats. Structure the report with an executive summary, key findings in tables and charts, and actionable recommendations. Use markdown formatting and include at least two charts.',
    icon: FileText,
    color: 'slate',
    category: 'Custom',
    keywords: ['custom', 'blank', 'own', 'free-form', 'anything'],
  },
];
